import type { Router } from 'vue-router';
import type { Vue } from '@sentry/vue/types/types';
import { IGNORE_ERRORS, API_ERROR_NAME, ALLOW_URLS } from './sentry.constants';
import type { SentryApiType } from './sentry.types';
import {
	init,
	withScope,
	setUser,
	captureException,
	captureMessage,
	browserTracingIntegration
} from '@sentry/vue';
import { AuthService } from '@/services/auth';

const appEnv = import.meta.env.VITE_ENV;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENV = ['production', 'staging'];

/**
 * Возвращает полный url запроса
 * @param {object} response - Запрос
 * @returns {string}
 */
const getResponseUrl = (response: any) => {
	let url = response.config.url;

	if (!url.includes('https')) {
		url = `${response.config.baseURL}/${url}`;
	}

	return url;
};

const getIntegrations = (router: Router) => {
	const integrations = [];

	if (appEnv === 'production') {
		integrations.push(
			browserTracingIntegration({
				router,
				routeLabel: 'path'
			})
		);
	}

	return integrations;
};

export const SentryService = {
	init(app: Vue, router: Router) {
		if (SENTRY_ENV.includes(appEnv)) {
			init({
				app,
				environment: appEnv,
				dsn: sentryDsn,
				enableTracing: true,
				tracesSampleRate: 1.0,
				trackComponents: true,
				// @ts-ignore - по какой-то причине Абу не видит 'unmount' хотя в документации и типах он есть
				hooks: ['activate', 'create', 'destroy', 'mount', 'unmount', 'update'],
				integrations: getIntegrations(router),
				ignoreErrors: IGNORE_ERRORS,
				allowUrls: ALLOW_URLS,
				beforeSend(event, hint) {
					const error = hint.originalException;

					if (error && typeof error === 'object' && 'message' in error) {
						const message = error.message;

						if (
							typeof message === 'string' &&
							IGNORE_ERRORS.some(sentence => message.indexOf(sentence) !== -1)
						) {
							return null;
						}
					}

					return event;
				}
			});
		}
	},
	captureApiException(response: any, type: SentryApiType = 'error') {
		withScope(scope => {
			const url = getResponseUrl(response);
			const name = `${API_ERROR_NAME}: ${url}`;
			const message = response.data?.message || response.message || '';
			const userId = AuthService.getUserId();

			// чтобы не показывать токен пользователя
			const headers = response.config.headers;
			delete headers.Authorization;

			const sentryUser = userId ? { id: userId } : null;
			setUser(sentryUser);
			scope.setLevel('error');
			scope.addBreadcrumb({
				category: 'data',
				level: 'info',
				message: JSON.stringify(
					{
						url,
						headers,
						params: response.config.params,
						request: JSON.parse(response.config.data || null),
						response: response.data || null
					},
					null,
					3
				)
			});

			if (type === 'error') {
				const error = new Error(message);
				error.name = name;
				return captureException(error);
			}

			return captureMessage(message, type);
		});
	},
	captureException(error: Error) {
		captureException(error);
	}
};
